/**
 * Formats seconds or string durations into clean 'M:SS' or 'H:MM:SS' time strings
 */
export function formatDuration(duration?: string | number | null): string {
  if (duration === undefined || duration === null || duration === "") return "0:00";
  if (typeof duration === "string") {
    if (duration.includes(":")) {
      return duration;
    }
    const parsed = parseFloat(duration);
    if (isNaN(parsed) || parsed <= 0) return "0:00";
    duration = parsed;
  }
  const totalSeconds = Math.floor(duration);
  if (totalSeconds <= 0) return "0:00";
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export interface LocalizationDictionary {
  home?: {
    views?: string;
    view?: string;
    hoursAgo?: string;
    daysAgo?: string;
    weeksAgo?: string;
    justNow?: string;
    recently?: string;
  };
  [key: string]: any;
}

/**
 * Parses raw view counts (e.g. 128400, "128.4K views", "1 view") and formats with localized strings
 */
export function formatLocalizedViews(views: string | number | undefined, t?: LocalizationDictionary): string {
  if (views === undefined || views === null || views === "") {
    const viewStr = t?.home?.view || "{{count}} view";
    return viewStr.replace("{{count}}", "0");
  }

  let countStr: string;
  let rawCount: number = 0;

  if (typeof views === "number") {
    rawCount = views;
    if (views >= 1_000_000) {
      countStr = `${(views / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    } else if (views >= 1_000) {
      countStr = `${(views / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    } else {
      countStr = views.toLocaleString();
    }
  } else {
    // string input, e.g. "128.4K views", "1 view", "45200"
    const cleaned = views.replace(/\s*(views?|vues?|kotala|mara\s*zilizotazamwa)\s*$/i, "").trim();
    countStr = cleaned;
    const num = parseFloat(cleaned.replace(/[^\d.]/g, ""));
    rawCount = isNaN(num) ? 1 : num;
  }

  const isPlural = rawCount !== 1;
  const template = isPlural
    ? (t?.home?.views || "{{count}} views")
    : (t?.home?.view || "{{count}} view");

  return template.replace("{{count}}", countStr);
}

/**
 * Formats relative dates (e.g. "2 hours ago", "1 day ago", "2026-03-01T...") into localized relative time strings
 */
export function formatLocalizedDate(dateInput: string | Date | undefined, t?: LocalizationDictionary): string {
  if (!dateInput) {
    return t?.home?.recently || "Recently";
  }

  // Handle existing relative strings (e.g. from SEED_VIDEOS: "2 hours ago", "1 day ago", "3 days ago", "1 week ago")
  if (typeof dateInput === "string") {
    const hoursMatch = dateInput.match(/^(\d+)\s+hours?\s+ago$/i);
    if (hoursMatch) {
      const template = t?.home?.hoursAgo || "{{count}} hours ago";
      return template.replace("{{count}}", hoursMatch[1]);
    }

    const daysMatch = dateInput.match(/^(\d+)\s+days?\s+ago$/i);
    if (daysMatch) {
      const template = t?.home?.daysAgo || "{{count}} days ago";
      return template.replace("{{count}}", daysMatch[1]);
    }

    const weeksMatch = dateInput.match(/^(\d+)\s+weeks?\s+ago$/i);
    if (weeksMatch) {
      const template = t?.home?.weeksAgo || "{{count}} weeks ago";
      return template.replace("{{count}}", weeksMatch[1]);
    }

    if (/^just now$/i.test(dateInput.trim())) {
      return t?.home?.justNow || "Just now";
    }

    if (/^recently$/i.test(dateInput.trim())) {
      return t?.home?.recently || "Recently";
    }
  }

  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return t?.home?.recently || "Recently";
  }

  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) {
    return t?.home?.justNow || "Just now";
  }
  if (diffHours < 24) {
    const template = t?.home?.hoursAgo || "{{count}} hours ago";
    return template.replace("{{count}}", diffHours.toString());
  }
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    const template = t?.home?.daysAgo || "{{count}} days ago";
    return template.replace("{{count}}", diffDays.toString());
  }
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    const template = t?.home?.weeksAgo || "{{count}} weeks ago";
    return template.replace("{{count}}", diffWeeks.toString());
  }

  return date.toLocaleDateString();
}

